import {
  ApiChartTemplate,
  ApiChartType,
  ApiDimension,
  ApiMetric,
} from "@incendium/api";
import { Box, Stack } from "@mui/material";
import ImpressionsByDayChart from "features/analytics/components/ImpressionsByDayChart";
import PrimaryTopMicroConversions from "features/analytics/components/PrimaryTopMicroConversions";
import { useMemo } from "react";
import {
  AnalyticsStatCard,
  IncreaseDecrease,
  JobInteractionsWheel,
  JobKeyStatsCard,
  percentageTopChangeByMetric,
  useFormatMetric,
} from "features/analytics";
import { StatCardTypography } from "Components/UI/StatCard";
import { AnalyticsSpacing } from "consts";
import GlassCard from "Components/GlassCard/GlassCard";

interface IJobSingleAndCategoryPageProps {
  dimension: ApiDimension;
  byDimension: string;
}

function JobSingleAndCategoryPage({
  dimension,
  byDimension,
}: IJobSingleAndCategoryPageProps) {
  const formatMetric = useFormatMetric();
  const attributes = useMemo(
    () => [
      {
        key: dimension,
        value: byDimension,
      },
    ],
    [dimension, byDimension]
  );

  return (
    <>
      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "repeat(12, 1fr)",
          // For xs, you might want rows to auto-adjust based on content
          gridTemplateRows: "auto",
          rowGap: AnalyticsSpacing,
          height: 840,
          columnGap: AnalyticsSpacing,
        }}
      >
        <Box sx={{ gridArea: "1 / 1 / 9 / 5" }}>
          <Stack spacing={AnalyticsSpacing} sx={{ height: "100%" }}>
            <AnalyticsStatCard
              chart={{
                name: "",
                dimension: [],
                template: ApiChartTemplate.SNAPSHOT,
                type: ApiChartType.GRAPH,
                attributes: [],
                yAxisKeys: [
                  {
                    key: "l",
                    fields: [ApiMetric.METRIC_PAGE_JOB_APPLICATIONS_COUNT],
                  },
                ],
              }}
              setDirection={(data, compData) =>
                percentageTopChangeByMetric(
                  data,
                  compData || [],
                  ApiMetric.METRIC_PAGE_JOB_APPLICATIONS_COUNT
                ) > 0
                  ? "up"
                  : "down"
              }
              renderBody={(data, compData) => {
                const change = percentageTopChangeByMetric(
                  data,
                  compData || [],
                  ApiMetric.METRIC_PAGE_JOB_APPLICATIONS_COUNT
                );
                return (
                  <>
                    <StatCardTypography size="small">
                      Total Applications
                    </StatCardTypography>
                    <StatCardTypography size="large">
                      {formatMetric(
                        ApiMetric.METRIC_PAGE_JOB_APPLICATIONS_COUNT,
                        Number(
                          data[0][
                          ApiMetric.METRIC_PAGE_JOB_APPLICATIONS_COUNT
                          ] || 0
                        )
                      )}
                    </StatCardTypography>
                    <StatCardTypography size="small">
                      <IncreaseDecrease
                        direction={change >= 0 ? "up" : "down"}
                        value={change}
                        size={"large"}
                        fontSize={22}
                      />
                    </StatCardTypography>
                  </>
                );
              }}
            />

            <JobKeyStatsCard
              jobDimension={dimension}
              selectedDimension={byDimension}
            />
          </Stack>
        </Box>
        <Box
          sx={{
            gridArea: "1 / 5 / 9 / -1",
          }}
        >
          <GlassCard height={"100%"} sx={{ height: "100%" }}>
            <JobInteractionsWheel attributes={attributes} />
          </GlassCard>
        </Box>
        <Box
          sx={{
            minHeight: 400,
            gridArea: "9 / 1 / auto / 7",
          }}
        >
          <ImpressionsByDayChart
            dimension={dimension}
            impressionField={ApiMetric.METRIC_JOB_IMPRESSIONS_COUNT}
            type="job"
            selected={byDimension}
          />
        </Box>
        <Box
          sx={{
            minHeight: 400,
            gridArea: "9 / 7 / auto / -1",
          }}
        >
          <PrimaryTopMicroConversions
            dimension={dimension}
            byDimension={byDimension || ""}
            dimensionString={`job`}
          />
        </Box>
      </Box>
    </>
  );
}

export default JobSingleAndCategoryPage;
